import { MINUTE_MS, SECOND_MS } from '@/constants';
import Hls from 'hls.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type Player = {
  ref: React.RefObject<HTMLVideoElement>;
  error: string | undefined;
  segmentUrl: string | undefined;
  src: (value?: string) => void;
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  isPlaying: boolean;
  isPaused: boolean;
  isLoaded: boolean;
  isSeekLoading: boolean;
  duration: number;
  currentTime: number;
  hls: Hls | null;
  setIsLoaded: (value: boolean) => void;
  previewThumbnailCount?: number;
  setPreviewThumbnailCount: (value?: number) => void;
};

export const usePlayer = () => {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string>();
  const [isSeekLoading, setIsSeekLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string>();
  const [segmentUrl, setSegmentUrl] = useState<string>();
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [hls, setHls] = useState<Hls | null>(null);
  const [previewThumbnailCount, setPreviewThumbnailCount] = useState<number>();

  const isPaused = useMemo(() => {
    return !isPlaying;
  }, [isPlaying]);

  const src = useCallback((value?: string) => {
    setSignedUrl(value);
  }, []);

  const play = useCallback(() => {
    const current = ref.current;
    if (isLoaded && current) {
      if (current.seeking) {
        const onSeeked = () => {
          if (current.ended) {
            current.dispatchEvent(new Event('ended'));
          } else {
            current.play();
          }
          current.removeEventListener('seeked', onSeeked);
        };

        current.pause();
        current.addEventListener('seeked', onSeeked);
      } else {
        if (current.ended) {
          current.dispatchEvent(new Event('ended'));
        } else {
          current.play();
        }
      }
    }
  }, [isLoaded]);

  const pause = useCallback(() => {
    const current = ref.current;
    if (isLoaded && current) {
      current.pause();
    }
  }, [isLoaded]);

  const seek = useCallback(
    (time: number) => {
      const current = ref.current;
      if (isLoaded && current && !Number.isNaN(current.duration)) {
        const targetTime = Math.min(Math.max(time, 0), current.duration);
        current.currentTime = targetTime;
      }
    },
    [isLoaded],
  );

  const player = useMemo<any>(() => {
    return {
      ref,
      src,
      segmentUrl,
      play,
      pause,
      seek,
      error,
      isLoaded,
      isPlaying,
      isPaused,
      isSeekLoading,
      duration,
      currentTime,
      hls,
      setIsLoaded,
      previewThumbnailCount,
      setPreviewThumbnailCount,
    };
  }, [
    src,
    segmentUrl,
    play,
    pause,
    seek,
    error,
    isLoaded,
    isPlaying,
    isPaused,
    isSeekLoading,
    duration,
    currentTime,
    hls,
    previewThumbnailCount,
  ]);

  useEffect(() => {
    const current = ref.current;

    if (current) {
      const onError = (e: Event) => {
        setError(e instanceof Error ? e.message : e.toString());
      };
      const onSeekLoading = () => {
        setIsSeekLoading(true);
      };
      const onLoadedMetadata = () => {
        setDuration(current.duration);
      };
      const onCanPlay = () => {
        setIsLoaded(true);
      };
      const onSeeked = () => {
        setIsSeekLoading(false);
      };
      const onPlay = () => {
        setIsPlaying(true);
        if (current.readyState >= 3) {
          setIsSeekLoading(false);
          setIsLoaded(true);
        }
      };
      const onPause = () => {
        setIsPlaying(false);
      };
      const onEnded = () => {
        current.pause();
      };
      const onTimeUpdate = () => {
        setCurrentTime(current.currentTime);
      };

      current.loop = false;

      current.addEventListener('error', onError);
      current.addEventListener('seeking', onSeekLoading);
      current.addEventListener('seeked', onSeeked);
      current.addEventListener('loadedmetadata', onLoadedMetadata);
      current.addEventListener('canplay', onCanPlay);
      current.addEventListener('play', onPlay);
      current.addEventListener('pause', onPause);
      current.addEventListener('ended', onEnded);
      current.addEventListener('timeupdate', onTimeUpdate);

      return () => {
        current.removeEventListener('error', onError);
        current.removeEventListener('seeking', onSeekLoading);
        current.removeEventListener('seeked', onSeeked);
        current.removeEventListener('loadedmetadata', onLoadedMetadata);
        current.removeEventListener('canplay', onCanPlay);
        current.removeEventListener('play', onPlay);
        current.removeEventListener('pause', onPause);
        current.removeEventListener('ended', onEnded);
        current.removeEventListener('timeupdate', onTimeUpdate);
      };
    }
  }, []);

  useEffect(() => {
    setDuration(0);
    setCurrentTime(0);
    setIsLoaded(false);
    setIsPlaying(false);
    setError(undefined);

    try {
      const current = ref.current;
      if (current && signedUrl) {
        const isHls = signedUrl.includes('.m3u8');

        if (isHls) {
          const updatedSignedUrl = signedUrl;
          const DEFAULT_POLICY = {
            default: {
              maxTimeToFirstByteMs: 30 * SECOND_MS,
              maxLoadTimeMs: MINUTE_MS,
              timeoutRetry: {
                maxNumRetry: Number.POSITIVE_INFINITY,
                retryDelayMs: SECOND_MS,
                maxRetryDelayMs: SECOND_MS,
              },
              errorRetry: {
                maxNumRetry: Number.POSITIVE_INFINITY,
                retryDelayMs: SECOND_MS,
                maxRetryDelayMs: SECOND_MS,
              },
            },
          };

          const hls = new Hls({
            xhrSetup: (xhr, url) => {
              const originalUrl = new URL(updatedSignedUrl);
              const segmentUrl = new URL(url);

              originalUrl.searchParams.forEach((value, key) => {
                segmentUrl.searchParams.set(key, value);
              });

              setSegmentUrl(segmentUrl.toString());

              xhr.open('GET', segmentUrl.toString());
            },
            playlistLoadPolicy: DEFAULT_POLICY,
            fragLoadPolicy: DEFAULT_POLICY,
            manifestLoadPolicy: DEFAULT_POLICY,
            // manifest에 CODECS 미선언 시 HE-AAC(mp4a.40.5)로 추론되는 것을 방지 (Tizen 무음 이슈)
            defaultAudioCodec: 'mp4a.40.2',
          });

          current.setAttribute('type', 'application/x-mpegURL');

          const MAX_MEDIA_RECOVER_ATTEMPTS = 3;
          let mediaRecoverAttempts = 0;
          let lastFatalErrorAt = 0;
          let audioCodecSwapped = false;

          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (!data.fatal) {
              return;
            }

            // 마지막 치명적 오류 이후 정상 재생이 지속됐다면 새로운 오류로 보고 복구 횟수 초기화
            const now = Date.now();
            if (now - lastFatalErrorAt > 10 * SECOND_MS) {
              mediaRecoverAttempts = 0;
            }
            lastFatalErrorAt = now;

            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR: {
                hls.startLoad();
                break;
              }
              case Hls.ErrorTypes.MEDIA_ERROR: {
                if (mediaRecoverAttempts < MAX_MEDIA_RECOVER_ATTEMPTS) {
                  mediaRecoverAttempts += 1;

                  // 복구가 반복되면 오디오 코덱 추론 오류(Tizen 무음 이슈)일 수 있어 코덱 교체 후 재복구
                  if (mediaRecoverAttempts > 1 && !audioCodecSwapped) {
                    audioCodecSwapped = true;
                    hls.swapAudioCodec();
                  }

                  hls.recoverMediaError();
                  current.play()?.catch?.(() => {});
                } else {
                  setError(data.details);
                }
                break;
              }
              default: {
                setError(data.details);
                break;
              }
            }
          });

          queueMicrotask(() => {
            hls.loadSource(signedUrl);
            hls.attachMedia(current);
          });

          setHls(hls);

          return () => {
            setHls(null);
            hls.destroy();
          };
        } else {
          current.src = signedUrl;
          current.load();
        }

        current.pause();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    }
  }, [signedUrl]);

  return player;
};
